// DrugMasterTab.tsx — direct view/edit of drug_master, plus Excel bulk
// upload. POC-only shortcut around the normalization-review workflow (see
// DrugReviewTab.tsx, which is untouched and still the long-term process) —
// "later we can disable it and make it more systemic" per the request that
// added this tab. See backend/routes/drug-master.js.
import { Fragment, useState, useEffect, useRef } from 'react';
import {
  listDrugMaster, createDrugMaster, updateDrugMaster,
} from '../api';
import { downloadDrugMasterTemplate, uploadDrugMasterExcel } from '@/api';
import type { DrugMasterRow, NewDrugMasterInput, DrugMasterBulkUploadResult } from '../types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const MEDICINE_TYPES = ['single', 'fdc'] as const;

const emptyDraft = (): NewDrugMasterInput => ({
  generic_name: '', strength: '', dosage_form: '', route: '', release_form: '',
  composition: '', medicine_type: 'single', active: true,
});

function DrugForm({ initial, onSave, onCancel, saving }: {
  initial: NewDrugMasterInput;
  onSave: (values: NewDrugMasterInput) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<NewDrugMasterInput>(initial);
  const set = (patch: Partial<NewDrugMasterInput>) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
      <Input placeholder="Generic name *" value={draft.generic_name || ''} onChange={(e) => set({ generic_name: e.target.value })} className="text-xs" />
      <Input placeholder="Strength (e.g. 500mg)" value={draft.strength || ''} onChange={(e) => set({ strength: e.target.value })} className="text-xs" />
      <Input placeholder="Dosage form (e.g. Tablet)" value={draft.dosage_form || ''} onChange={(e) => set({ dosage_form: e.target.value })} className="text-xs" />
      <Input placeholder="Route (e.g. Oral)" value={draft.route || ''} onChange={(e) => set({ route: e.target.value })} className="text-xs" />
      <Input placeholder="Release form (e.g. SR)" value={draft.release_form || ''} onChange={(e) => set({ release_form: e.target.value })} className="text-xs" />
      <select
        value={draft.medicine_type || 'single'}
        onChange={(e) => set({ medicine_type: e.target.value as 'single' | 'fdc' })}
        style={{ padding: '6px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border, #E5E7EB)', fontSize: 12 }}
      >
        {MEDICINE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <Input placeholder="Composition (for FDCs)" value={draft.composition || ''} onChange={(e) => set({ composition: e.target.value })} className="text-xs" style={{ gridColumn: 'span 2' }} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <input type="checkbox" checked={draft.active !== false} onChange={(e) => set({ active: e.target.checked })} /> Active
      </label>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
        <Button size="sm" disabled={saving || !(draft.generic_name || '').trim()} onClick={() => onSave(draft)}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function DrugMasterTab() {
  const [rows, setRows] = useState<DrugMasterRow[] | null>(null);
  const [q, setQ] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<DrugMasterBulkUploadResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    listDrugMaster({ q: q.trim() || undefined, active: activeFilter === 'all' ? undefined : activeFilter === 'active' })
      .then(setRows)
      .catch((e: any) => setError(e.message));
  };
  useEffect(() => { load(); }, [activeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (id: number | null, values: NewDrugMasterInput) => {
    setSaving(true); setError('');
    try {
      if (id) await updateDrugMaster(id, values);
      else await createDrugMaster(values);
      setCreating(false); setEditingId(null);
      load();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true); setError(''); setUploadResult(null);
    try {
      const result = await uploadDrugMasterExcel(file);
      setUploadResult(result);
      load();
    } catch (e: any) { setError(e.message); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14, lineHeight: 1.6 }}>
        Direct view/edit of drug_master, for POC data loading — this bypasses the Drug Review approval
        queue on purpose (that workflow is unaffected and still the intended long-term process). Download
        the current data as an Excel sheet, edit it, and re-upload to bulk create/update rows: leave "id"
        blank for a new drug, fill it in to edit an existing one.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 14 }}>
        <Input placeholder="Search generic name…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} className="text-xs" style={{ maxWidth: 220 }} />
        <Button variant="ghost" size="sm" onClick={load}>Search</Button>
        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as typeof activeFilter)}
          style={{ padding: '6px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border, #E5E7EB)', fontSize: 12 }}
        >
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
          <option value="all">All</option>
        </select>
        <div style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" onClick={() => downloadDrugMasterTemplate().catch((e: any) => setError(e.message))}>⬇ Download template</Button>
        <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={(e) => handleUpload(e.target.files?.[0])} />
        <Button variant="ghost" size="sm" disabled={uploading} loading={uploading} onClick={() => fileRef.current?.click()}>⬆ Upload Excel</Button>
        {!creating && <Button size="sm" onClick={() => setCreating(true)}>+ New Drug</Button>}
      </div>

      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      {uploadResult && (
        <div style={{ marginBottom: 14, padding: '10px 12px', background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 'var(--radius)', fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#065F46', marginBottom: 4 }}>
            Processed {uploadResult.processed} row{uploadResult.processed === 1 ? '' : 's'}: {uploadResult.created} created, {uploadResult.updated} updated, {uploadResult.failed} failed.
          </div>
          {uploadResult.failed > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, color: '#991B1B' }}>
              {uploadResult.results.filter((r) => r.status === 'error').map((r, i) => (
                <li key={i}>Row {r.row}: {r.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {creating && (
        <Card className="p-3.5 mb-3">
          <DrugForm initial={emptyDraft()} saving={saving} onCancel={() => setCreating(false)} onSave={(v) => save(null, v)} />
        </Card>
      )}

      {!rows && <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading…</div>}

      {rows && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#6B7280', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Generic</th>
                <th style={{ padding: '6px 8px' }}>Strength</th>
                <th style={{ padding: '6px 8px' }}>Form</th>
                <th style={{ padding: '6px 8px' }}>Route</th>
                <th style={{ padding: '6px 8px' }}>Type</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
                <th style={{ padding: '6px 8px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.generic_name}</td>
                    <td style={{ padding: '6px 8px' }}>{r.strength || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.dosage_form || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.route || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.medicine_type}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <span className="badge" style={r.active ? { background: '#D1FAE5', color: '#065F46', border: '1px solid #A7F3D0' } : { background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }}>
                        {r.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <button
                        type="button"
                        style={{ fontSize: 11, color: 'var(--teal, #0B5FD6)', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}
                        onClick={() => setEditingId(editingId === r.id ? null : r.id)}
                      >
                        {editingId === r.id ? 'Close' : 'Edit'}
                      </button>
                    </td>
                  </tr>
                  {editingId === r.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: '10px 8px', background: '#F9FAFB' }}>
                        <DrugForm
                          initial={{
                            canonical_name: r.canonical_name, generic_name: r.generic_name, strength: r.strength || '',
                            dosage_form: r.dosage_form || '', route: r.route || '', release_form: r.release_form || '',
                            composition: r.composition || '', medicine_type: r.medicine_type, active: r.active,
                          }}
                          saving={saving}
                          onCancel={() => setEditingId(null)}
                          onSave={(v) => save(r.id, v)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>No drugs found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
